import { Content } from '@volkswagen-onehub/fa-content-service';

import { FeatureAppMode } from '../index';
import { FeatureAppTrackingEvent, FeatureAppTrackingManager } from '../typings/tagging';
import { formatNumberWithCommas, parseMileageNumber } from '../utils/general';
import { getDefaultTrackingEvent } from '../hooks/use-tracking-manager';
import { OwnersLiteratureDocumentsData } from '../typings/general';

interface AdditionalTrackingData {
  modelYear?: string;
  trim?: string;
  modelName?: string;
  vin?: string;
  odometer?: string | number;
}

interface LinkContent {
  name?: string;
  url?: string;
}

export const onVWBasicButtonClickEvent = (
  trackingManager: FeatureAppTrackingManager,
  contentData: Content,
  additionalTrackingData?: AdditionalTrackingData,
  FAMode: FeatureAppMode = 'literature'
) => {
  let customEvent = getDefaultTrackingEvent();

  if (FAMode === 'maintenance') {
    customEvent.environment = {
      ...customEvent.environment,
      featureAppId: 'Maintenance-Schedule-D6-US'
    }
  }

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    //@ts-ignore
    contentId: contentData && contentData.Name || 'Basic button click',
  };

  if (additionalTrackingData) {
    const { modelName, modelYear, odometer, trim } = additionalTrackingData;
    const mileage = formatNumberWithCommas(parseMileageNumber(odometer) ?? 0) ?? 0;

    const vehicleData = {
      carlineName: modelName,
      mileage,
      model: trim,
      modelYear,
    }

    customEvent.ownership = {
      ...customEvent.ownership,
      vehicle: [vehicleData]
    };
  }

  trackingManager &&
    trackingManager.trackMaintenanceGlobalEvent(
      'VWBasic_Button_Click',
      customEvent,
    );
};

export const onVWBasicLinkClickEvent = (
  trackingManager: FeatureAppTrackingManager,
  contentData?: Content,
  mode: 'Link' | 'Download' = 'Link',
  document?: OwnersLiteratureDocumentsData,
  additionalTrackingData?: AdditionalTrackingData,
  FAMode: FeatureAppMode = 'literature',
  linkContent?: LinkContent
) => {
  let customEvent: FeatureAppTrackingEvent = getDefaultTrackingEvent();

  if (FAMode === 'maintenance') {
    customEvent.environment = {
      ...customEvent.environment,
      featureAppId: 'Maintenance-Schedule-D6-US'
    }
  }

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    //@ts-ignore
    contentId: contentData && contentData.Name || `Basic ${mode} click`,
  };

  if (linkContent) {
    const { name, url } = linkContent;

    customEvent.link = {
      ...customEvent.link,
      name: name,
      url: url
    };
  }

  if (document) {
    customEvent.file = {
      ...customEvent.file,
      category: document.document_type || undefined,
      language: document.language_code.split('-')[0] || 'en',
      name: document.file_name,
      type: document.file_name.includes('.pdf') ? 'pdf' : undefined,
      url: undefined
    }
  }

  if (additionalTrackingData) {
    const { modelName, modelYear, odometer, trim, vin } = additionalTrackingData;

    const vehicleData = {
      carlineName: modelName,
      manufacturer: 'VW',
      mileage: Number(odometer ?? 0) ?? 0,
      model: trim,
      modelYear,
      vin
    }

    customEvent.ownership = {
      ...customEvent.ownership,
      vehicle: [vehicleData]
    };
  }

  trackingManager &&
    trackingManager.trackMaintenanceGlobalEvent(
      `VWBasic_${mode}_Click`,
      customEvent,
    );
};

export const onVWBasicModeChangeEvent = (
  trackingManager: FeatureAppTrackingManager,
  contentData: Content,
  mode: 'Model Details' | 'VIN'
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: mode,
  };
  customEvent.content = contentData;

  trackingManager &&
    trackingManager.trackMaintenanceGlobalEvent(
      'VWBasic_Button_Click',
      customEvent,
    );
}

export const onVWBasicPageLoadEvent = (
  trackingManager: FeatureAppTrackingManager,
  FAMode: FeatureAppMode = 'literature'
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: `${FAMode === 'maintenance' ? 'Maintenance Schedule' : "Owners's Literature"} page load`,
  };

  if (FAMode === 'maintenance') {
    customEvent.environment = {
      ...customEvent.environment,
      featureAppId: 'Maintenance-Schedule-D6-US'
    }
  }

  trackingManager &&
    trackingManager.trackMaintenanceGlobalEvent(
      'VWBasic_GenericFeatureApp_Load',
      customEvent,
    );
};

export const onVWBasicLayerLoad = (
  trackingManager: FeatureAppTrackingManager,
  FAMode: FeatureAppMode = 'literature',
  vehicle?: AdditionalTrackingData
) => {
  let customEvent = getDefaultTrackingEvent();

  if (FAMode === 'maintenance') {
    customEvent.environment = {
      ...customEvent.environment,
      featureAppId: 'Maintenance-Schedule-D6-US'
    }
  }

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Recommended Services Layer load',
  };

  let ownershipVehicle: any = {
    manufacturer: 'Volkswagen',
    carlineName: vehicle?.modelName,
    modelYear: vehicle?.modelYear,
  }

  if (vehicle?.vin) ownershipVehicle.vehicleId = vehicle.vin;
  if (vehicle?.odometer) {
    ownershipVehicle.mileage = vehicle.odometer;
    ownershipVehicle.mileageUnit = "mi"
  }
  if (vehicle?.trim) ownershipVehicle.model = vehicle.trim;

  customEvent.ownership = {
    ...customEvent.ownership,
    vehicle: [ownershipVehicle]
  }

  trackingManager &&
    trackingManager.trackMaintenanceGlobalEvent(
      'VWBasic_Layer_Load',
      customEvent,
    );
}

export const onVWBasicLayerClose = (
  trackingManager: FeatureAppTrackingManager,
  FAMode: FeatureAppMode = 'literature',
  vehicle?: AdditionalTrackingData
) => {
  let customEvent = getDefaultTrackingEvent();

  if (FAMode === 'maintenance') {
    customEvent.environment = {
      ...customEvent.environment,
      featureAppId: 'Maintenance-Schedule-D6-US'
    }
  }

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Recommended Services Layer closed',
  };

  let ownershipVehicle: any = {
    manufacturer: 'Volkswagen',
    carlineName: vehicle?.modelName,
    modelYear: vehicle?.modelYear,
  }

  if (vehicle?.vin) ownershipVehicle.vehicleId = vehicle.vin;
  if (vehicle?.odometer) {
    ownershipVehicle.mileage = vehicle.odometer;
    ownershipVehicle.mileageUnit = "mi"
  }
  if (vehicle?.trim) ownershipVehicle.model = vehicle.trim;

  customEvent.ownership = {
    ...customEvent.ownership,
    vehicle: [ownershipVehicle]
  }

  trackingManager &&
    trackingManager.trackMaintenanceGlobalEvent(
      'VWBasic_LayerClose_Click',
      customEvent,
    );
}
